<script setup lang="ts">
import { Badge, Card, Icon, ProgressBar } from "@/design-system";
import { fmtTime } from "@/domain/calc";
import type { Discipline } from "@/domain/calc";
import type { Plan } from "@/composables/usePlan";

/** Step 3b -- the roll-up across every leg (FR-3.2). */
const props = defineProps<{ plan: Plan }>();
const p = props.plan;

// Total over Discipline, so the lookup cannot come back undefined and quietly
// paint a leg in the default colour.
const LEG_COLOR: Record<Discipline, string> = {
  swim: "var(--fuel-fluid)",
  bike: "var(--fuel-carb)",
  run: "var(--fuel-sodium)",
};

const peak = () => Math.max(...p.legResults.value.map((r) => r.res.totalCarbs), 1);
const r1 = (n: number) => Math.round(n * 10) / 10;
</script>

<template>
  <div class="totals">
    <Card
      variant="brand"
      data-testid="totals-card"
    >
      <div class="totals__head">
        <div>
          <p class="cw-eyebrow totals__eyebrow">
            {{ p.t.value.avgPerHour }}
          </p>
          <p class="totals__metric">
            <span data-testid="totals-perhour">{{ r1(p.totals.value.perHour) }}</span>
            <span class="totals__unit">g/h</span>
          </p>
        </div>
        <Badge :tone="p.totalsVerdict.value.tone">
          {{ p.totalsVerdict.value.label }}
        </Badge>
      </div>
      <div class="totals__grid">
        <div>
          <p class="cw-eyebrow totals__eyebrow">
            {{ p.t.value.totalCarbs }}
          </p>
          <p class="totals__cell">
            {{ r1(p.totals.value.carbs) }} <span class="totals__unit">g</span>
          </p>
        </div>
        <div>
          <p class="cw-eyebrow totals__eyebrow">
            {{ p.t.value.kcal }}
          </p>
          <p class="totals__cell">
            {{ Math.round(p.totals.value.kcal) }}
          </p>
        </div>
        <div>
          <p class="cw-eyebrow totals__eyebrow">
            {{ p.t.value.totalTime }}
          </p>
          <p class="totals__cell">
            {{ fmtTime(p.totals.value.minutes) }} <span class="totals__unit">h</span>
          </p>
        </div>
      </div>
    </Card>

    <Card :title="p.t.value.perLeg">
      <div
        v-for="r in p.legResults.value"
        :key="r.key"
        class="totals__leg"
        :data-leg-row="r.key"
      >
        <div class="totals__leg-head">
          <span class="totals__leg-name">
            <Icon
              :name="r.label.icon"
              :size="17"
            />
            {{ r.label.name }}
          </span>
          <span class="totals__leg-carbs">{{ r1(r.res.totalCarbs) }} g</span>
        </div>
        <ProgressBar
          :value="r.res.totalCarbs"
          :max="peak()"
          :color="LEG_COLOR[r.type]"
        />
        <p class="totals__leg-meta">
          {{ fmtTime(r.res.minutesAvg) }} h · {{ r1(r.res.perHour.avg) }} g/h ·
          {{ r.res.itemCount }} {{ p.t.value.items }} ·
          {{ r.res.vesselsPerRace === null ? "—" : Math.ceil(r.res.vesselsPerRace) }}
          {{ r.type === "run" ? p.t.value.cups : p.t.value.bottles }}
        </p>
      </div>
    </Card>
  </div>
</template>

<style scoped>
.totals { display: flex; flex-direction: column; gap: var(--space-6); }
.totals__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-5);
}
.totals__eyebrow { margin: 0; color: var(--pine-200); }
.totals__metric {
  margin: 2px 0 0;
  font: var(--type-metric);
  color: var(--sand-50);
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
}
.totals__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-5);
  margin-top: var(--space-7);
}
.totals__cell {
  margin: 2px 0 0;
  font: var(--type-metric-sm);
  color: var(--sand-50);
  font-variant-numeric: tabular-nums;
}
.totals__unit {
  font: var(--type-unit);
  color: var(--pine-200);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}
.totals__leg + .totals__leg { margin-top: var(--space-7); }
.totals__leg-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  margin-bottom: var(--space-4);
}
.totals__leg-name { display: flex; align-items: center; gap: var(--space-4); }
.totals__leg-carbs {
  font: var(--type-metric-sm);
  font-variant-numeric: tabular-nums;
  color: var(--text-strong);
}
.totals__leg-meta {
  margin: var(--space-4) 0 0;
  font: var(--type-unit);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
</style>
